import {
  QUESTION_TYPE_FILL_IN_THE_BLANKS,
  QUESTION_TYPE_MULTIPLE_CHOICE,
  QUESTION_TYPE_TRUE_OR_FALSE,
  QUESTION_UID_TEXT_RESPONSE_V3,
  QUESTION_UID_MULTI_CHOICE,
  QUESTION_UID_TRUE_FALSE_V2,
  QUESTION_UID_BLANKS,
  FIELD_UID_ANSWER,
  FIELD_UID_BLANKS,
  FIELD_UID_BLANKS_ANSWERS,
  FIELD_UID_MCQ_ANSWERS,
  FIELD_UID_MCQ_CHOICES,
  FIELD_UID_RADIO,
  FIELD_UID_SCORE,
  FIELD_UID_SETTINGS,
  FIELD_UID_TITLE,
  AI_QUESTION_DEFAULT_SCORE,
  QUESTION_TYPE_INLINE_TEXT,
  CONTENT_UID_TEXT_BOX,
  FIELD_UID_RICH_TEXT_EDITOR,
} from "Worksheet/modules/WorksheetConstants";
import { t } from "i18next";

import moment from "moment";

const getQuestionTypeConfig = presetTemplateId => {
  const config = require(`AppComponents/Worksheet/templates/questionTypeTemplates/${presetTemplateId}.json`);
  return config;
};

const questionConfigs = {
  "Text response": {
    id: "232",
    uid: QUESTION_UID_TEXT_RESPONSE_V3,
    config: getQuestionTypeConfig(QUESTION_UID_TEXT_RESPONSE_V3),
  },
  "Multiple choice": {
    id: "234",
    uid: QUESTION_UID_MULTI_CHOICE,
    config: getQuestionTypeConfig(QUESTION_UID_MULTI_CHOICE),
  },
  "True or false": {
    id: "231",
    uid: QUESTION_UID_TRUE_FALSE_V2,
    config: getQuestionTypeConfig(QUESTION_UID_TRUE_FALSE_V2),
  },
  "Fill in the blanks": {
    id: "233",
    uid: QUESTION_UID_BLANKS,
    config: getQuestionTypeConfig(QUESTION_UID_BLANKS),
  },
  [QUESTION_TYPE_INLINE_TEXT]: {
    id: "65",
    uid: CONTENT_UID_TEXT_BOX,
    config: getQuestionTypeConfig(CONTENT_UID_TEXT_BOX),
  },
};

/**
 * Generates the field template configuration for a given question type.
 *
 * @param {Object} fieldSet - An object containing the IDs for various fields.
 * @param {string} questionType - The type of question to generate the field template for.
 * @returns {Array} An array of field configuration objects.
 *
 * The function creates a common set of field configurations and then
 * appends additional fields based on the specified question type.
 *
 * Common fields include 'title', 'settings', and 'score'.
 * For 'Multiple choice' questions, an additional 'choices' field is added.
 * For 'Fill in the blanks' questions, an additional 'blanks' field is added.
 */

const getFieldTemplate = (fieldSet, questionType) => {
  const commonFields = [
    {
      id: fieldSet.titleId,
      uid: FIELD_UID_TITLE,
      value: null,
      resolveFields: {
        id: fieldSet.titleId,
        type: "UNDEFINED_ENTITY",
        __typename: "UndefinedEntity",
      },
      resolvedMinimalTree: {
        __typename: "UndefinedEntity",
      },
      __typename: "QuestionFields",
    },
    {
      id: fieldSet.settingsId,
      uid: FIELD_UID_SETTINGS,
      value: null,
      resolveFields: {
        id: fieldSet.settingsId,
        type: "UNDEFINED_ENTITY",
        __typename: "UndefinedEntity",
      },
      resolvedMinimalTree: {
        __typename: "UndefinedEntity",
      },
      __typename: "QuestionFields",
    },
    {
      id: fieldSet.marksId,
      uid: FIELD_UID_SCORE,
      value: AI_QUESTION_DEFAULT_SCORE,
      resolveFields: {
        id: fieldSet.marksId,
        type: "UNDEFINED_ENTITY",
        __typename: "UndefinedEntity",
      },
      resolvedMinimalTree: {
        __typename: "UndefinedEntity",
      },
      __typename: "QuestionFields",
    },
  ];

  switch (questionType) {
    case QUESTION_TYPE_MULTIPLE_CHOICE:
      return [
        ...commonFields,
        {
          id: fieldSet.choicesId,
          uid: FIELD_UID_MCQ_CHOICES,
          value: [
            {
              id: fieldSet.choice1,
              value: `${t("ai:generating_options")}...`,
            },
          ],
          resolveFields: {
            id: fieldSet.choicesId,
            type: "UNDEFINED_ENTITY",
            __typename: "UndefinedEntity",
          },
          resolvedMinimalTree: {
            __typename: "UndefinedEntity",
          },
          __typename: "QuestionFields",
        },
      ];
    case QUESTION_TYPE_FILL_IN_THE_BLANKS:
      return [
        ...commonFields,
        {
          id: fieldSet.blanksId,
          uid: FIELD_UID_BLANKS,
          value: null,
          resolveFields: {
            id: fieldSet.blanksId,
            type: "UNDEFINED_ENTITY",
            __typename: "UndefinedEntity",
          },
          resolvedMinimalTree: {
            __typename: "UndefinedEntity",
          },
          __typename: "QuestionFields",
        },
      ];
    case QUESTION_TYPE_INLINE_TEXT:
      return [
        {
          id: fieldSet.inlineTextId,
          uid: FIELD_UID_RICH_TEXT_EDITOR,
          value: null,
          resolveFields: {
            id: fieldSet.inlineTextId,
            type: "UNDEFINED_ENTITY",
            __typename: "UndefinedEntity",
          },
          resolvedMinimalTree: {
            __typename: "UndefinedEntity",
          },
          __typename: "QuestionFields",
        },
      ];
    default:
      return commonFields;
  }
};

/**
 * Generates the answer field template configuration for a given question type.
 *
 * @param {Object} fieldSet - An object containing the IDs for various answer fields.
 * @param {string} questionType - The type of question to generate the answer field template for.
 * @returns {Array} An array of answer field configuration objects.
 *
 * The function creates a common set of answer field configurations and then
 * appends additional fields based on the specified question type.
 *
 * Common answer fields include the 'answer' field.
 * For 'Multiple choice' questions, an additional 'answers' field is added.
 * For 'True or false' questions, an additional 'radio' field is added.
 * For 'Fill in the blanks' questions, an additional 'blanksAnswers' field is added.
 */

const getAnswerFieldsTemplate = (fieldSet, questionType) => {
  const commonAnswerFields = [
    {
      id: fieldSet.answerTextId,
      uid: FIELD_UID_ANSWER,
      value: null,
      resolveFields: {
        id: fieldSet.answerTextId,
        type: "UNDEFINED_ENTITY",
        __typename: "UndefinedEntity",
      },
      resolvedMinimalTree: {
        __typename: "UndefinedEntity",
      },
      __typename: "QuestionFields",
    },
  ];

  switch (questionType) {
    case QUESTION_TYPE_MULTIPLE_CHOICE:
      return [
        ...commonAnswerFields,
        {
          id: fieldSet.answersTextId,
          uid: FIELD_UID_MCQ_ANSWERS,
          value: null,
          resolveFields: {
            id: fieldSet.answersTextId,
            type: "UNDEFINED_ENTITY",
            __typename: "UndefinedEntity",
          },
          resolvedMinimalTree: {
            __typename: "UndefinedEntity",
          },
          __typename: "QuestionFields",
        },
      ];
    case QUESTION_TYPE_TRUE_OR_FALSE:
      return [
        ...commonAnswerFields,
        {
          id: fieldSet.radioId,
          uid: FIELD_UID_RADIO,
          value: null,
          resolveFields: {
            id: fieldSet.radioId,
            type: "UNDEFINED_ENTITY",
            __typename: "UndefinedEntity",
          },
          resolvedMinimalTree: {
            __typename: "UndefinedEntity",
          },
          __typename: "QuestionFields",
        },
      ];
    case QUESTION_TYPE_FILL_IN_THE_BLANKS:
      return [
        ...commonAnswerFields,
        {
          id: fieldSet.blankAnswerId,
          uid: FIELD_UID_BLANKS_ANSWERS,
          value: null,
          resolveFields: {
            id: fieldSet.blankAnswerId,
            type: "UNDEFINED_ENTITY",
            __typename: "UndefinedEntity",
          },
          resolvedMinimalTree: {
            __typename: "UndefinedEntity",
          },
          __typename: "QuestionFields",
        },
      ];
    case QUESTION_TYPE_INLINE_TEXT: {
      return [];
    }
    default:
      return commonAnswerFields;
  }
};

/**
 * @deprecated Use `getQuestionTemplateV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
/**
 * Generates a question template object for a given question ID, field set, and question type.
 *
 *
 * @param {Object} fieldSet - An object containing the IDs for various fields.
 * @param {string} questionType - The type of question to generate the template for.
 * @returns {Object} A question template object structured according to the provided parameters.
 *
 * The function uses predefined configurations for different question types from `questionConfigs`.
 * It populates the question template with details such as resource type, item metadata, fields, and answer fields.
 *
 * - `getFieldTemplate` and `getAnswerFieldsTemplate` functions are called to generate the respective field configurations.
 *
 * The returned object contains comprehensive information about the question, ready to be used in the application.
 */
export const getQuestionTemplate = (fieldSet, questionType) => {
  const config = questionConfigs[questionType];
  return {
    node: {
      id: fieldSet.nodeId,
      resourceType: "QUESTION",
      item: {
        id: fieldSet.questionId,
        questionType: {
          id: config.id,
          uid: config.uid,
          config: config.config,
          __typename: "QuestionType",
        },
        libraryUid: null,
        questionScore:
          questionType === QUESTION_TYPE_INLINE_TEXT
            ? null
            : AI_QUESTION_DEFAULT_SCORE,
        questionTitle: {
          id: "",
          uid: "",
          value: "",
        },
        updatedAt: moment.now(),
        metadata: {
          added_to_school_library: false,
        },
        presetQuestionId: null,
        assessmentTools: {
          totalCount: 0,
          edges: [],
          __typename: "AssessmentToolConnection",
        },
        fields: getFieldTemplate(fieldSet, questionType),
        answerFields: getAnswerFieldsTemplate(fieldSet, questionType),
        librarySubjects: [],
        associatedEntities: [],
        aiConversation: {
          edges: [],
          __typename: "AiConversationConnection",
        },
        __typename: "Question",
      },
      isSavedForLibrary: false,
      isLibraryPremium: false,
      showLibraryPremiumBannerForUser: false,
      childrenV2: {
        resourceTypeBasedCount: {
          totalCount: 0,
          __typename: "OrganizationResourceCount",
        },
        edges: [],
        __typename: "OrganizationResourceConnection",
      },
      __typename: "OrganizationResource",
    },
    __typename: "OrganizationResourceEdge",
  };
};

/**
 * Generates a question template object for a given question ID, field set, and question type.
 * @param {Object} fieldSet - An object containing the IDs for various fields.
 * @param {string} questionType - The type of question to generate the template for.
 * @returns {Object} A question template object structured according to the provided parameters.
 * The function uses predefined configurations for different question types from `questionConfigs`.
 * It populates the question template with details such as resource type, item metadata, fields, and answer fields.
 * `getFieldTemplate` and `getAnswerFieldsTemplate` functions are called to generate the respective field configurations.
 * The returned object contains comprehensive information about the question, ready to be used in the application.
 */
export const getQuestionTemplateV2 = (fieldSet, questionType) => {
  const config = questionConfigs[questionType];
  return {
    id: fieldSet.questionId,
    parentIdV2: fieldSet.parentId,
    parentTypeV2: "QUESTION",
    sequence: 0,
    questionType: {
      id: config.id,
      uid: config.uid,
      config: config.config,
      __typename: "QuestionType",
    },
    questionScoreV2:
      questionType === QUESTION_TYPE_INLINE_TEXT
        ? null
        : AI_QUESTION_DEFAULT_SCORE,
    questionTitleV2: {
      id: "",
      uid: "",
      value: "",
    },
    updatedAt: moment.now(),
    libraryUid: null,
    metadata: {
      added_to_school_library: false,
    },
    presetQuestionId: null,
    children: {
      edges: [],
      __typename: "QuestionChildrenConnection",
    },
    assessmentTools: {
      totalCount: 0,
      edges: [],
      __typename: "AssessmentToolConnection",
    },
    fields: getFieldTemplate(fieldSet, questionType),
    answerFields: getAnswerFieldsTemplate(fieldSet, questionType),
    librarySubjects: [],
    associatedEntities: [],
    aiConversation: {
      edges: [],
      __typename: "AiConversationConnection",
    },
    __typename: "Question",
  };
};
